// ===== PermissionsConsentDialog.jsx - FIXED: Checkbox selection working properly =====

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Article as PostIcon,
  Visibility as ViewIcon,
  Group as FollowersIcon,
  Description as ContentIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const PermissionsConsentDialog = ({ open, onClose, onAccept, platform }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State for tracking which permissions user has explicitly selected
  // ✅ CRITICAL: Start with EMPTY array - user must select everything
  const [acceptedPermissions, setAcceptedPermissions] = useState([]);
  const [allRequiredAccepted, setAllRequiredAccepted] = useState(false);

  // Define permissions based on platform
  const permissions = {
    facebook: [
      {
        name: 'pages_show_list',
        icon: <ViewIcon color="primary" />,
        title: 'View Your Pages',
        description: 'See the list of Facebook Pages you manage',
        allowedUsage: [
          'Show you the list of Pages you manage',
          'Verify that you manage a Page',
          'Allow you to select which Pages to connect'
        ],
        dependencies: 'None',
        required: true
      },
      {
        name: 'pages_read_engagement',
        icon: <FollowersIcon color="primary" />,
        title: 'Read Page Content & Engagement',
        description: 'Read content posted by your Pages and follower data',
        allowedUsage: [
          'View content posted on your Pages (posts, photos)',
          'Access follower information (names, profile pictures)',
          'Read Page metadata and insights'
        ],
        dependencies: 'Requires: pages_show_list',
        required: true
      },
      {
        name: 'business_management',
        icon: <BusinessIcon color="primary" />,
        title: 'Access Business Manager',
        description: 'Access Pages managed through Facebook Business Manager',
        allowedUsage: [
          'View Pages managed through Business Manager',
          'Access Instagram Business accounts linked to Business Manager Pages',
          'Connect Business Manager Pages to Revfer'
        ],
        dependencies: 'Requires: pages_show_list, pages_read_engagement',
        note: 'Required as dependency for accessing Instagram Business accounts through Business Manager',
        required: true
      },
      {
        name: 'pages_manage_posts',
        icon: <PostIcon color="primary" />,
        title: 'Create & Manage Page Posts',
        description: 'Create, edit, and delete posts on your Facebook Pages',
        allowedUsage: [
          'Publish posts, photos, to your Pages',
          'Update existing posts on your Pages',
          'Delete posts from your Pages'
        ],
        dependencies: 'Requires: pages_show_list, pages_read_engagement',
        primary: true,
        required: true
      },
      {
        name: 'instagram_basic',
        icon: <InstagramIcon sx={{ color: '#E4405F' }} />,
        title: 'Access Instagram Profile Info',
        description: 'Read basic information from your Instagram Business accounts',
        allowedUsage: [
          'Get Instagram Business account username and ID',
          'Access Instagram profile metadata',
          'View profile information for connected accounts'
        ],
        dependencies: 'Requires: pages_show_list',
        required: true
      },
      {
        name: 'instagram_content_publish',
        icon: <ContentIcon sx={{ color: '#E4405F' }} />,
        title: 'Post to Instagram',
        description: 'Create and publish photos to your Instagram Business accounts',
        allowedUsage: [
          'Publish photos to Instagram feed on your behalf',
          'Manage organic content for your business Instagram accounts'
        ],
        dependencies: 'Requires: instagram_basic, pages_show_list, pages_read_engagement',
        primary: true,
        required: true
      }
    ]
  };

  const platformPermissions = permissions[platform] || permissions.facebook;
  const requiredPermissions = platformPermissions.filter(p => p.required);
  const optionalPermissions = platformPermissions.filter(p => !p.required);

  // ✅ CRITICAL: Reset to empty when dialog opens
  useEffect(() => {
    if (open) {
      console.log('🔓 Dialog opened - resetting permissions (user must select all)');
      setAcceptedPermissions([]);
      setAllRequiredAccepted(false);
    }
  }, [open]);

  // ✅ Check if all required permissions are selected whenever acceptedPermissions changes
  useEffect(() => {
    const allRequired = requiredPermissions.every(p => acceptedPermissions.includes(p.name));
    setAllRequiredAccepted(allRequired);
    console.log(`✅ Progress: ${acceptedPermissions.length}/${requiredPermissions.length} required permissions selected`);
  }, [acceptedPermissions, requiredPermissions]);

  // ✅ FIX: Simplified toggle handler
  const handlePermissionToggle = (permissionName) => {
    setAcceptedPermissions(prev => {
      if (prev.includes(permissionName)) {
        // Remove if already selected
        return prev.filter(p => p !== permissionName);
      } else {
        // Add if not selected
        return [...prev, permissionName];
      }
    });
  };

  const handleAcceptAll = () => {
    console.log('📝 User clicked "Select All" - selecting all permissions');
    const allPerms = platformPermissions.map(p => p.name);
    setAcceptedPermissions(allPerms);
  };

  const handleAccept = () => {
    console.log('✅ User accepted permissions:', acceptedPermissions);
    // Pass accepted permissions to parent
    onAccept(acceptedPermissions);
  };

  // Calculate progress percentage
  const progressPercent = Math.round((acceptedPermissions.length / requiredPermissions.length) * 100);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>
              Review & Accept Permissions
            </Typography>
          </Box>
          <IconButton onClick={onClose} size={isMobile ? "small" : "medium"}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Introduction */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="600" gutterBottom>
            ⚠️ Action Required: Explicitly Grant Permissions
          </Typography>
          <Typography variant="caption">
            Revfer needs these permissions to post AI-generated content to your Facebook Pages 
            and Instagram Business accounts. <strong>You must manually check each permission below</strong> to proceed.
          </Typography>
        </Alert>

        {/* Progress Indicator */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: allRequiredAccepted ? 'success.lighter' : 'warning.lighter',
            border: '1px solid',
            borderColor: allRequiredAccepted ? 'success.light' : 'warning.light'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight="600">
              {allRequiredAccepted ? '✅ All Required Permissions Selected' : '⏳ Please Select All Permissions'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {acceptedPermissions.length}/{requiredPermissions.length}
            </Typography>
          </Box>
          
          <LinearProgress 
            variant="determinate" 
            value={progressPercent} 
            sx={{ 
              height: 8, 
              borderRadius: 1,
              mb: 1
            }}
            color={allRequiredAccepted ? "success" : "warning"}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              {allRequiredAccepted 
                ? 'You can now proceed to Facebook' 
                : 'Check each permission to continue'}
            </Typography>
            {!allRequiredAccepted && (
              <Button
                variant="text"
                size="small"
                onClick={handleAcceptAll}
                sx={{ fontSize: '0.75rem' }}
              >
                Select All
              </Button>
            )}
          </Box>
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Please click each checkbox below</strong> to explicitly grant each permission:
        </Typography>

        {/* Required Permissions Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            p: 1,
            bgcolor: 'warning.lighter',
            borderRadius: 1
          }}>
            <WarningIcon color="warning" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="600">
              Required Permissions ({requiredPermissions.length})
            </Typography>
            <Chip 
              label="MUST SELECT ALL" 
              size="small" 
              color="warning"
              sx={{ ml: 'auto' }}
            />
          </Box>

          <List sx={{ bgcolor: 'background.paper' }}>
            {requiredPermissions.map((permission, index) => {
              const isSelected = acceptedPermissions.includes(permission.name);
              
              return (
                <React.Fragment key={permission.name}>
                  <ListItem 
                    alignItems="flex-start"
                    sx={{ 
                      py: 2,
                      bgcolor: permission.primary ? 'primary.lighter' : 'transparent',
                      borderRadius: 1,
                      mb: 1,
                      border: isSelected ? '2px solid' : '1px solid',
                      borderColor: isSelected ? 'success.main' : 'divider',
                      // ✅ FIX: Remove cursor pointer and onClick from ListItem
                      transition: 'all 0.2s'
                    }}
                  >
                    {/* ✅ FIX: Checkbox with proper event handling */}
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation(); // Prevent event bubbling
                          handlePermissionToggle(permission.name);
                        }}
                        onClick={(e) => e.stopPropagation()} // Prevent event bubbling on click too
                        color="success"
                        sx={{
                          '& .MuiSvgIcon-root': {
                            fontSize: 28
                          }
                        }}
                      />
                    </ListItemIcon>

                    {/* Icon */}
                    <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                      {permission.icon}
                    </ListItemIcon>

                    {/* ✅ FIX: Make text clickable to toggle */}
                    <ListItemText
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handlePermissionToggle(permission.name)}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight="600">
                            {permission.title}
                          </Typography>
                          {permission.primary && (
                            <Chip 
                              label="Core Feature" 
                              size="small" 
                              color="primary" 
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {permission.required && (
                            <Chip 
                              label="Required" 
                              size="small" 
                              color="warning" 
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {isSelected && (
                            <CheckIcon 
                              color="success" 
                              fontSize="small"
                              sx={{ ml: 'auto' }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {permission.description}
                          </Typography>
                          
                          <Typography variant="caption" fontWeight="600" display="block" sx={{ mt: 1, mb: 0.5 }}>
                            What Revfer Can Do:
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {permission.allowedUsage.map((usage, idx) => (
                              <li key={idx}>
                                <Typography variant="caption" color="text.secondary">
                                  {usage}
                                </Typography>
                              </li>
                            ))}
                          </Box>

                          {permission.dependencies && permission.dependencies !== 'None' && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                mt: 1, 
                                display: 'block',
                                color: 'text.disabled',
                                fontStyle: 'italic'
                              }}
                            >
                              {permission.dependencies}
                            </Typography>
                          )}

                          {permission.note && (
                            <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                              <Typography variant="caption">
                                <strong>Note:</strong> {permission.note}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < requiredPermissions.length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </Box>

        {/* Optional Permissions Section (if any) */}
        {optionalPermissions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 2,
              p: 1,
              bgcolor: 'info.lighter',
              borderRadius: 1
            }}>
              <InfoIcon color="info" fontSize="small" />
              <Typography variant="subtitle2" fontWeight="600">
                Optional Permissions ({optionalPermissions.length})
              </Typography>
            </Box>

            <List sx={{ bgcolor: 'background.paper' }}>
              {optionalPermissions.map((permission, index) => {
                const isSelected = acceptedPermissions.includes(permission.name);
                
                return (
                  <React.Fragment key={permission.name}>
                    <ListItem 
                      alignItems="flex-start"
                      sx={{ 
                        py: 2,
                        borderRadius: 1,
                        mb: 1,
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'success.main' : 'divider',
                        transition: 'all 0.2s'
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handlePermissionToggle(permission.name);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          color="success"
                        />
                      </ListItemIcon>

                      <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                        {permission.icon}
                      </ListItemIcon>

                      <ListItemText
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handlePermissionToggle(permission.name)}
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="600">
                              {permission.title}
                            </Typography>
                            <Chip 
                              label="Optional" 
                              size="small" 
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {isSelected && (
                              <CheckIcon 
                                color="success" 
                                fontSize="small"
                                sx={{ ml: 'auto' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={permission.description}
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
        )}

        {/* Important Notes */}
        <Box sx={{ mt: 3 }}>
          <Alert severity="success" icon={<CheckIcon />}>
            <Typography variant="body2" fontWeight="600" gutterBottom>
              Your Control & Security
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>
                <Typography variant="caption">
                  <strong>You choose what to connect:</strong> After granting permissions, you'll 
                  select specific pages and Instagram accounts
                </Typography>
              </li>
              <li>
                <Typography variant="caption">
                  <strong>Review before posting:</strong> All posts require your approval before publishing
                </Typography>
              </li>
              <li>
                <Typography variant="caption">
                  <strong>Revoke anytime:</strong> Remove Revfer's access from Facebook Settings → 
                  Business Integrations
                </Typography>
              </li>
              <li>
                <Typography variant="caption">
                  <strong>Secure connection:</strong> OAuth 2.0 standard ensures your password is never 
                  shared with Revfer
                </Typography>
              </li>
            </Box>
          </Alert>
        </Box>

        {/* What Happens Next */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="600" gutterBottom>
            What Happens Next:
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2 }}>
            <li>
              <Typography variant="caption">
                You'll be redirected to Facebook to log in
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                Facebook will show you the permissions you just accepted
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                You'll select which Pages, Businesses, and Instagram accounts to connect
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                You'll see a confirmation screen showing everything you connected
              </Typography>
            </li>
            <li>
              <Typography variant="caption">
                Start creating and posting AI-generated content!
              </Typography>
            </li>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
        {/* Permission Summary with Warning */}
        <Paper 
          elevation={0}
          sx={{ 
            width: '100%',
            p: 1.5, 
            bgcolor: allRequiredAccepted ? 'success.lighter' : 'error.lighter',
            border: '1px solid',
            borderColor: allRequiredAccepted ? 'success.light' : 'error.light'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {allRequiredAccepted ? (
              <CheckIcon color="success" />
            ) : (
              <WarningIcon color="error" />
            )}
            <Typography variant="caption" fontWeight="600" display="block">
              {allRequiredAccepted 
                ? '✅ All required permissions selected - ready to proceed' 
                : `⚠️ Please select all ${requiredPermissions.length} required permissions to continue`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip 
              label={`${acceptedPermissions.length} of ${requiredPermissions.length} selected`}
              size="small"
              color={allRequiredAccepted ? "success" : "error"}
              icon={allRequiredAccepted ? <CheckIcon /> : <WarningIcon />}
            />
            <Chip 
              label={`${requiredPermissions.length} required`}
              size="small"
              variant="outlined"
            />
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Button 
            onClick={onClose} 
            fullWidth={isMobile}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAccept}
            fullWidth={isMobile}
            startIcon={allRequiredAccepted ? <CheckIcon /> : <WarningIcon />}
            disabled={!allRequiredAccepted}
            color={allRequiredAccepted ? "primary" : "warning"}
          >
            {allRequiredAccepted 
              ? `I Accept ${acceptedPermissions.length} Permissions & Continue` 
              : `Select All ${requiredPermissions.length} Required Permissions`}
          </Button>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          {allRequiredAccepted 
            ? 'By continuing, you explicitly grant these permissions to Revfer' 
            : 'You must check all required permissions above before proceeding'}
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsConsentDialog;